import axios from 'axios';
import * as tus from 'tus-js-client';
// Import media types from the shared types file
import {
  MediaObject,
  MediaDirectory,
  MediaResponse,
  StorageUsageStatus,
  StorageStatusResponse,
  DEFAULT_PER_REQUEST_LIMIT,
} from '../types/media-types';
// Import the shared AI Agent API service
import { aiAgentApiService } from 'src/services/api/ai-agent-api.service';
import { Logger } from 'src/utils/logger';

// MediaObject, MediaResponse, and MediaDirectory interfaces are now imported from types/media-types.ts

import { FormMediaObject } from 'src/components/Form/S3UploadInput';

// Import environment configuration and auth utilities
import env from 'src/env.json';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { useMediaDirectoryStore } from '../store/use-media-directory-store';

// Define the base URLs for different APIs
// Main BeCreatives backend API URL - this is the correct URL for all media operations
const BE_API_URL = env.API_URL || 'http://localhost/api';

// Custom error class for directory operations
class DirectoryError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'DirectoryError';
    this.statusCode = statusCode;
  }
}

export class MediaService {
  // Error message constants
  private static readonly NON_EMPTY_FOLDER_ERROR_MESSAGE = 'Cannot delete non-empty folder';

  // Static variables for tracking requests and caching
  private static initialLoadMade = false;
  private static mediaRequestCount = 0;
  private static pendingRequests = new Map<string, Promise<any>>();
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static mediaApiLocked = false;
  private static lastRequestTimestamps = new Map<string, number>();

  private static mediaApiQueue: Array<
    | {
        resolve: (value: MediaResponse) => void;
        reject: (reason: any) => void;
        directoryId: string | null | undefined;
        fullPath?: string;
        requestType?: 'media';
      }
    | {
        resolve: (value: any) => void;
        reject: (reason: any) => void;
        directoryId: string;
        fullPath?: string;
        requestType: 'subdirectory';
      }
    | {
        resolve: (value: any) => void;
        reject: (reason: any) => void;
        requestType: 'directories';
      }
  > = [];

  // Directory structure caching
  private static directoryTreePromise: Promise<any> | null = null; // For storing hierarchical directory tree promise
  private static directoryTreeTimestamp = 0; // Timestamp for directory tree cache
  private static directoryStructureTimestamp = 0;
  private static DIRECTORY_CACHE_TTL = 30000; // 30 seconds cache validity for directory structure

  // API URL for all requests
  private readonly _beApiUrl: string;
  private agentId: string | null = null; // Will be fetched from the API

  private readonly CACHE_TTL = 15000; // 15 seconds cache validity

  constructor(beApiUrl: string = BE_API_URL) {
    this._beApiUrl = beApiUrl;

    // Initialize agent ID when service is created
    this.initializeAgentId();
  }

  // Get the base URL for API calls
  get beApiUrl() {
    return this._beApiUrl;
  }

  // Helper method to get auth headers for API calls
  get headers(): Record<string, string> {
    // Get authentication token from AuthUtils
    const jwt = AuthUtils.getJwt();
    const token = jwt.token;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      // Add Bearer token for authentication with the BE API
      Authorization: token ? `Bearer ${token}` : '',
    };

    // Add x-brand-id header if a brand is selected
    const selectedBrandId = this.getSelectedBrandId();
    if (selectedBrandId) {
      headers['x-brand-id'] = selectedBrandId;
    }

    return headers;
  }

  /**
   * Get the selected brand ID from cookie (same as BrandSelectionContext uses)
   */
  private getSelectedBrandId(): string | null {
    const BRAND_COOKIE_NAME = 'selectedBrandId';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${BRAND_COOKIE_NAME}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Clear all media library caches when switching accounts
   * This ensures fresh data is loaded for the new account
   */
  public clearCache(): void {
    // Clear all static cache data
    MediaService.cache.clear();
    MediaService.pendingRequests.clear();
    MediaService.lastRequestTimestamps.clear();

    // Reset directory tree promise
    MediaService.directoryTreePromise = null;

    // Reset loading flags
    MediaService.initialLoadMade = false;
    MediaService.mediaRequestCount = 0;

    // Release any locks
    MediaService.mediaApiLocked = false;

    // Clear the queue
    MediaService.mediaApiQueue = [];

    // Reset agent ID to force re-fetch for new account
    this.agentId = null;
  }

  // Public methods to get API URL and headers for direct calls
  public getApiUrl() {
    return this.beApiUrl;
  }

  /**
   * Helper method to determine MIME type from a filename
   * @param fileName The name of the file to determine MIME type for
   * @returns The MIME type string
   */
  getMimeTypeFromName(fileName: string): string {
    // Extract extension from filename
    const extension = fileName.split('.').pop()?.toLowerCase();

    // Map common extensions to MIME types
    const mimeTypes: { [key: string]: string } = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      svg: 'image/svg+xml',
      webp: 'image/webp',
      mp4: 'video/mp4',
      webm: 'video/webm',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      ogg: 'audio/ogg',
      m4a: 'audio/mp4',
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };

    return extension && mimeTypes[extension] ? mimeTypes[extension] : 'application/octet-stream';
  }

  public async getHeaders() {
    return this.headers;
  }

  // Import the shared AI Agent API service
  private aiAgentApiService = aiAgentApiService;

  // Helper to directly fetch root media bypassing all queues, locks and caches
  public async getRootMedia(): Promise<MediaResponse> {
    const storeState = useMediaDirectoryStore.getState();
    try {
      // Get agent ID
      const agent_id = await this.getAgentId();

      // Get search query from store if it exists
      const searchQuery = storeState.searchQuery;

      // Build params object with conditional search query
      const params: Record<string, any> = {
        agent_id,
        sortBy: 'created_at',
        sortOrder: 'desc',
      };

      // Add search query if it exists
      if (searchQuery && searchQuery.trim() !== '') {
        storeState.setLoadingSearch(true);
        params.query = searchQuery.trim();

        // Clear any duplicate request timestamps when performing an explicit search
        // This ensures search requests always go through
        MediaService.lastRequestTimestamps.clear();
      }

      const rootData = await this.aiAgentApiService.getMediaItems(params, true);

      storeState.setLoadingSearch(false);

      // Ensure currentPath is empty for root
      if (!rootData.currentPath) {
        rootData.currentPath = [];
      } else if (rootData.currentPath.length > 0) {
        rootData.currentPath = [];
      }

      // Clear any root cache and update it with fresh data
      MediaService.cache.delete('media-items-root');
      MediaService.cache.set('media-items-root', {
        data: rootData,
        timestamp: Date.now(),
      });

      return rootData;
    } catch (error) {
      storeState.setLoadingSearch(false);
      console.error(`❌ Error in direct root API call:`, error);
      throw error;
    }
  }

  /**
   * Fetches the agent ID from the backend
   * This should be called before any API requests that require the agent_id
   * Uses the shared AI Agent API service instead of making a direct API call
   */
  private async initializeAgentId(): Promise<void> {
    try {
      const agentId = await this.aiAgentApiService.getAgentId();

      if (agentId) {
        // Store the agent ID for future use
        this.agentId = agentId;
      } else {
        console.error('⚠️ Shared service returned empty agent ID');
        this.fallbackToDevAgentId();
      }
    } catch (error: any) {
      console.error('❌ Error initializing agent ID from shared service:', error.message);
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error response:', {
          status: error.response.status,
          data: error.response.data,
        });
      }

      // Fall back to development ID if available
      this.fallbackToDevAgentId();
    }
  }

  /**
   * Helper method to provide a fallback agent ID in development
   */
  private fallbackToDevAgentId(): void {
    // Development fallback
    if (process.env.NODE_ENV === 'development') {
      this.agentId = '60389db9-91dc-49de-aa85-7400c6a3ade6'; // Test agent ID
      console.warn('⚠️ Using development fallback agent ID:', this.agentId);
    }
  }

  /**
   * Gets the agent ID, fetching it first if not already available
   * Uses the shared AI Agent API service directly
   */
  private async getAgentId(): Promise<string> {
    try {
      // If we already have an agent ID cached, return it
      if (this.agentId) {
        return this.agentId;
      }

      // Use the shared service to get the agent ID
      const agentId = await this.aiAgentApiService.getAgentId();

      // Store it locally for future use
      this.agentId = agentId;

      return this.agentId;
    } catch (error) {
      console.error('Error getting agent ID:', error);

      // Development fallback - only use it if the API call fails
      if (process.env.NODE_ENV === 'development') {
        this.agentId = '60389db9-91dc-49de-aa85-7400c6a3ade6'; // Test agent ID
        console.warn('Using development fallback agent ID in getAgentId:', this.agentId);
        return this.agentId;
      }

      throw new Error('Failed to get agent ID. Please try again later.');
    }
  }

  // Centralized lock-based media items fetcher
  // This implementation ensures that only ONE call to the media API can happen at a time
  // All other concurrent requests are either served from cache or queued
  async getMediaItems(
    directoryId?: string | null,
    forceRefresh = false,
    fullPath?: string,
    mimeType?: string,
    searchQuery?: string,
    brandId?: string, // Brand ID to pass as user_id parameter for brand-specific filtering
  ): Promise<MediaResponse> {
    // If no search query was provided explicitly, get it from the store
    // This ensures search queries are preserved across directory navigation
    if (searchQuery === undefined) {
      const storeState = useMediaDirectoryStore.getState();
      searchQuery = storeState.searchQuery;
    }

    // Create a unique cache key for this request
    // Include mime type in the cache key if filtering is applied
    // CRITICAL FIX: Ensure search query is part of the cache key
    const trimmedSearchQuery = searchQuery ? searchQuery.trim() : '';
    const hasSearchQuery = trimmedSearchQuery !== '';
    const cacheKey = `media-items-${directoryId || 'root'}${mimeType && mimeType !== 'all' ? `-${mimeType}` : ''}${hasSearchQuery ? `-search-${trimmedSearchQuery}` : ''}`;
    const requestKey = `get-${cacheKey}`;

    // Track request count for debugging
    MediaService.mediaRequestCount++;

    // CRITICAL: Check for duplicate requests within a short time window
    // This prevents the multiple refetch issue during directory navigation with search
    const lastRequestTimestamp = MediaService.lastRequestTimestamps.get(cacheKey);
    const now = Date.now();
    const isDuplicateRequest = lastRequestTimestamp && now - lastRequestTimestamp < 500; // 500ms window

    // Store this request timestamp
    MediaService.lastRequestTimestamps.set(cacheKey, now);

    // Skip duplicate requests unless forced
    if (isDuplicateRequest && !forceRefresh) {
      // Return from cache if available or wait for the pending request
      const cachedData = MediaService.cache.get(cacheKey);
      if (cachedData) {
        return cachedData.data;
      }
      if (MediaService.pendingRequests.has(requestKey)) {
        return MediaService.pendingRequests.get(requestKey)!;
      }
    }

    // If we're forcing a refresh, returning to root, or have a search query, skip the cache and clear any existing cache entry
    if (forceRefresh || directoryId === null || hasSearchQuery) {
      // Delete the existing cache entry if there is one
      MediaService.cache.delete(cacheKey);

      // When returning to root, clear ALL directory caches to ensure fresh data
      if (directoryId === null) {
        // Clear any cache entries related to directories
        for (const key of [...MediaService.cache.keys()]) {
          if (key.startsWith('media-items-')) {
            MediaService.cache.delete(key);
          }
        }
      }

      // CRITICAL: Also remove any pending request for this directory
      // This ensures we make a fresh API call rather than reusing an in-progress one
      if (MediaService.pendingRequests.has(requestKey)) {
        MediaService.pendingRequests.delete(requestKey);
      }
    } else {
      // CACHE CHECK: Check the cache for non-forced requests
      const cachedData = MediaService.cache.get(cacheKey);
      if (cachedData && Date.now() - cachedData.timestamp < this.CACHE_TTL) {
        return cachedData.data;
      }

      // PENDING REQUEST CHECK: If the same request is already in progress, reuse its promise
      // (Only for non-forced requests)
      if (MediaService.pendingRequests.has(requestKey)) {
        return MediaService.pendingRequests.get(requestKey)!;
      }
    }

    // GLOBAL API LOCK CHECK: If ANY media API request is currently in progress
    if (MediaService.mediaApiLocked) {
      // Directory switching and root navigation always get priority - force unlock the API
      // Root directory (null) is special and always gets highest priority
      const isRootNavigation = directoryId === null;
      if (forceRefresh || isRootNavigation) {
        // Special log for root navigation to help debugging
        if (isRootNavigation) {
        } else {
        }

        // Release the lock for this high-priority request
        MediaService.mediaApiLocked = false;

        // Save the original queue so we can restore it later
        const originalQueue = [...MediaService.mediaApiQueue];
        MediaService.mediaApiQueue = [];

        // For root navigation, clear all caches to ensure fresh data
        if (isRootNavigation) {
          // Clear ALL cache entries to ensure completely fresh data
          MediaService.cache.clear();
          MediaService.pendingRequests.clear();
        }

        // Schedule the queue restoration for after this critical request completes
        setTimeout(() => {
          MediaService.mediaApiQueue = [...originalQueue];
          if (originalQueue.length > 0) {
            this.processQueuedRequests();
          }
        }, 500);

        // Continue with the high-priority request (falls through to code below)
      } else {
        // For normal (non-forced) requests, queue them as usual

        // Create a promise that will be resolved when the lock is released
        const queuedPromise = new Promise<MediaResponse>((resolve, reject) => {
          // Add this request to the queue
          MediaService.mediaApiQueue.push({ resolve, reject, directoryId });
        });

        // Store this promise so identical requests can reuse it while it's in the queue
        MediaService.pendingRequests.set(requestKey, queuedPromise);
        return queuedPromise;
      }
    }

    // LOCK ACQUISITION: We can proceed with the actual API call

    // If this is a force refresh, log that we're definitely making an API call
    if (forceRefresh) {
    }
    MediaService.mediaApiLocked = true;

    // Create and store the new request promise
    const requestPromise = new Promise<MediaResponse>(async (resolve, reject) => {
      try {
        // Get agent ID
        const agent_id = await this.getAgentId();

        // Prepare the correct path parameter based on directory ID or full path
        let pathParam: string | undefined = undefined;

        // Get state from store once upfront - critical for consistent decision making
        const storeState = useMediaDirectoryStore.getState();
        const directlyClickedDirectory = storeState.directClicked;

        if (directlyClickedDirectory === true) {
          // CRITICAL FIX: For back navigation (direct clicks), always ignore fullPath
          // This prevents incorrect directory content from showing when navigating back
          fullPath = undefined;
        }
        // Only use fullPath when NOT in direct navigation mode
        else if (fullPath && !directlyClickedDirectory) {
          // Clean the path by removing any ID prefix that might be present
          pathParam = this.cleanApiPath(fullPath);
        } else if (directoryId) {
          // COMPLETE OVERHAUL OF DIRECTORY PATH RESOLUTION
          // Addressing persistent issues with back navigation and directory caching

          // We already have the store state and directClicked flag from above

          // CRITICAL: Always use exactly what was clicked, ignoring any previous state
          // This overrides all other rules and cached paths

          // Clean the directory ID of any prefixes to get the basic name
          let cleanDirectoryId = directoryId;

          // Clean any prefixes from directory ID
          if (cleanDirectoryId?.startsWith('dir-root-')) {
            cleanDirectoryId = cleanDirectoryId.substring('dir-root-'.length);
          } else if (cleanDirectoryId?.startsWith('dir-child-')) {
            const match = cleanDirectoryId.match(/^dir-child-(.+)$/);
            if (match && match[1]) {
              cleanDirectoryId = match[1];

              // If it still contains slashes (e.g. "ttt/ppp"), take just the directory portion
              // This is needed to prevent nested path when a directory ID contains slashes
              if (cleanDirectoryId.includes('/')) {
                // For a path like "ttt/ppp", we need to determine what part to keep
                // If it was direct clicked, we only care about "ppp"
                // If it was otherwise requested, we want the full path
                if (directlyClickedDirectory) {
                  // For direct clicks, just use the final part
                  const parts = cleanDirectoryId.split('/');
                  cleanDirectoryId = parts[parts.length - 1];
                }
              }
            }
          }

          // Now with a clean directory ID, set the path param based on strictest priority ordering
          if (directlyClickedDirectory) {
            // ULTRA AGGRESSIVE FIX: Always use just the exact clean directory name for direct clicks
            // and back button navigation, completely ignoring any stored paths
            if (cleanDirectoryId && cleanDirectoryId.includes('/')) {
              // If even after cleaning we still have a path with slashes, just use the final part
              const parts = cleanDirectoryId.split('/');
              pathParam = parts[parts.length - 1];
            } else {
              // Otherwise use the clean single directory name
              pathParam = cleanDirectoryId;
            }

            // Reset the store path to prevent path accumulation
            useMediaDirectoryStore.getState().forceDirectoryPath(pathParam || null);
          }
          // Everything else is lower priority and only used if not a direct click
          else {
            // Check if we need to use a path parameter
            // CRITICAL CHECK: We must check directClick status again to ensure we NEVER use fullPath during back navigation
            const isDirectNavigation = useMediaDirectoryStore.getState().directClicked;

            if (isDirectNavigation) {
              // BACK NAVIGATION: Always use directoryId and ignore any fullPath that might be set
              if (directoryId && directoryId !== 'root') {
                pathParam = directoryId;
              }
            } else if (fullPath) {
              // If a specific fullPath was provided, prioritize it
              if (fullPath) {
                pathParam = this.cleanApiPath(fullPath);
              }
            } else if (directoryId && directoryId !== 'root') {
              // Fallback to using directoryId
              pathParam = directoryId;
            }
          }

          // FINAL CHECK for directory navigation
          const finalCheckDirectClicked = useMediaDirectoryStore.getState().directClicked;

          if (finalCheckDirectClicked && directoryId && directoryId !== 'root') {
            // BACK NAVIGATION: Force pathParam to be ONLY the directoryId for back navigation
            // This is our final safeguard
            pathParam = directoryId;
          } else if (!finalCheckDirectClicked && fullPath) {
            // FORWARD NAVIGATION: Make sure we're using the full path for subdirectory navigation
            pathParam = this.cleanApiPath(fullPath);
          }

          // CRITICAL DEBUGGING - Log the parameters being used for this request

          // CRITICAL: Log the final path being used for debugging

          // This is vital: preserve the full nested path structure (e.g., "ttt/ppp")
          // Do not modify or split the path further as it would break nested directory navigation
        }

        // CRITICAL FIX: Search string issue - ensure query parameter is properly formed
        // Map our parameters to what the backend expects
        const params: Record<string, string> = {
          agent_id,
          // Only include path if we have one, and make sure it's a string
          // CRITICAL: Ensure the path is ALWAYS cleaned before using in API call
          ...(pathParam ? { path: this.cleanApiPath(pathParam) } : {}),
          // Add mime_type filter if provided and it's not 'all'
          ...(mimeType && mimeType !== 'all' ? { mime_type: mimeType } : {}),
          // CRITICAL: Add brand ID as user_id parameter for brand-specific filtering
          ...(brandId ? { user_id: brandId } : {}),
          sortBy: 'created_at',
          sortOrder: 'desc',
        };

        // CRITICAL: Add search query as a separate step to ensure it's included
        if (searchQuery && searchQuery.trim() !== '') {
          params.query = searchQuery.trim();

          // CRITICAL FIX: For searches in subdirectories, ensure we have the proper hierarchical path
          // This ensures search works properly in all directory contexts
          if (directoryId && fullPath && directoryId !== 'root') {
            // Make sure we're using the full hierarchical path for search in subdirectories
            // This fixes the "Missing hierarchical path" issue
            params.path = this.cleanApiPath(fullPath);
          }

          // Make direct API call with search parameters

          const apiResponse = await this.aiAgentApiService.getMediaItems(params, forceRefresh);

          // Ensure we have a properly structured response with both files and folders arrays
          // Map the properties to match the expected structure by UI components
          const mappedFiles = (apiResponse.files || []).map((file: any) => ({
            id: file.id,
            file_name: file.name || '', // Map name to file_name
            mime_type: file.mime_type || this.getMimeTypeFromName(file.name || ''),
            size: file.size || 0,
            url: file.url || '',
            directory_id: directoryId,
            created_at: file.created_at || new Date().toISOString(),
            updated_at: file.updated_at || file.created_at || new Date().toISOString(),
            tags: file.tags || [],
            metadata: file.metadata || {},
            // AI-generated analysis fields
            generated_description: file.generated_description || '',
            generated_tags: file.generated_tags || [],
            analyze_state: file.analyze_state,
            description: file.description || '',
          }));

          const response = {
            files: mappedFiles,
            folders: apiResponse.folders || [],
            currentPath: apiResponse.currentPath || [],
          };

          // Update cache with fresh search results
          MediaService.cache.set(cacheKey, {
            data: response,
            timestamp: now,
          });

          // Release the lock
          MediaService.mediaApiLocked = false;

          // Process queued requests
          setTimeout(() => this.processQueuedRequests(), 0);

          // Return search results
          resolve(response);
          return;
        } else {
          // Always force fresh results when searching or switching directories
          // This ensures we always get the latest data
          if (searchQuery || forceRefresh) {
            MediaService.cache.delete(cacheKey);
          }

          // Extreme safety measure - double check search query is in params if it should be
          if (searchQuery && searchQuery.trim() !== '' && !params.query) {
            console.warn('⚠️ CRITICAL: Search query missing from params, forcing inclusion');
            params.query = searchQuery.trim();
          }

          // CRITICAL FIX: Create a direct fetch instead of using axios to ensure parameters are included
          // This bypasses any potential axios configuration issues
          const url = new URL(`${this.beApiUrl}/ai-agent/media`);
          // Add base parameters
          Object.entries(params).forEach(([key, value]) => {
            url.searchParams.append(key, value);
          });

          // CRITICAL: Directly ensure search query is added to URL parameters if it exists
          if (hasSearchQuery) {
            // First, remove any existing search or query parameters (in case they were added incorrectly)
            url.searchParams.delete('query');
            url.searchParams.delete('search');
            url.searchParams.delete('q');

            // Add the search query with multiple possible parameter names to catch the right one
            url.searchParams.set('query', trimmedSearchQuery);
            url.searchParams.set('search', trimmedSearchQuery);
            url.searchParams.set('q', trimmedSearchQuery);

            // FORCE the query parameter to be included by checking it's there
            const hasQuery = url.searchParams.has('query');

            if (!hasQuery) {
              // Last resort: force it again with different methods
              url.searchParams.append('query', trimmedSearchQuery);
            }

            // Additional safeguard - verify the URL string actually contains the query
            if (!url.toString().includes('query=')) {
              console.error('⚠️ CRITICAL: Query parameter not showing up in URL string!');
            }
          }

          // Log the EXACT URL string that will be requested
          const requestUrl = url.toString();
          // CRITICAL: Double-check the URL has the search query if it should
          if (hasSearchQuery && !requestUrl.includes('query=')) {
            console.error(
              '%c⚠️ EMERGENCY: Search query still not in URL after all attempts!',
              'background: #ff0000; color: white; padding: 2px 5px; border-radius: 3px',
              {
                searchQuery: trimmedSearchQuery,
                currentUrl: requestUrl,
              },
            );

            // Add the search query as a direct axios parameter as a final fallback
            const baseUrl = this.beApiUrl + '/ai-agent/media';

            // Create a new params object that explicitly includes the search query
            // This ensures it doesn't get lost in URL encoding or other issues
            const axiosParams = {
              agent_id: params.agent_id,
              query: trimmedSearchQuery,
              sortBy: params.sortBy || 'created_at',
              sortOrder: params.sortOrder || 'desc',
              // Include any other parameters from the URL
              ...Object.fromEntries(url.searchParams.entries()),
            };

            // Make the request with direct parameters
            const response = await axios.get(baseUrl, {
              headers: this.headers,
              params: axiosParams,
            });

            // Process the response data directly instead of using a separate method
            if (response.data) {
              // Process the files to ensure they have all required properties
              if (response.data.files) {
                // Process files directly inline instead of using a separate method
                response.data.files = response.data.files
                  .map((file: any) => {
                    // Apply MIME type filtering if specified
                    if (mimeType && file.mime_type && !file.mime_type.startsWith(mimeType)) {
                      return null; // This will be filtered out later
                    }

                    // Ensure all required properties are present including AI analysis fields
                    return {
                      ...file,
                      id: file.id || '',
                      name: file.name || file.fileName || 'Unnamed file',
                      mime_type:
                        file.mime_type ||
                        ((fileName) => {
                          // Extract extension from filename
                          const extension = fileName.split('.').pop()?.toLowerCase() || '';

                          // Map common extensions to MIME types
                          const mimeTypes: { [key: string]: string } = {
                            jpg: 'image/jpeg',
                            jpeg: 'image/jpeg',
                            png: 'image/png',
                            gif: 'image/gif',
                            pdf: 'application/pdf',
                            doc: 'application/msword',
                            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            xls: 'application/vnd.ms-excel',
                            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          };

                          return mimeTypes[extension] || 'application/octet-stream';
                        })(file.name || file.fileName || ''),
                      size: file.size || 0,
                      url: file.url || '',
                      created_at: file.created_at || new Date().toISOString(),
                      // Ensure AI analysis fields are preserved from spread
                      analyze_state: file.analyze_state,
                      generated_description: file.generated_description || '',
                      generated_tags: file.generated_tags || [],
                    };
                  })
                  .filter(Boolean); // Remove any null entries from MIME type filtering
              }

              // Store the response in cache AFTER processing to include analyze_state
              MediaService.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });

              return response.data;
            }

            return { files: [], folders: [] };
          } else {
            // Capture actual request for debugging
            const axiosInstance = axios.create();
            axiosInstance.interceptors.request.use((request) => {
              return request;
            });
            // Use the shared AI Agent API service with forceRefresh parameter
            // This ensures consistent behavior with the RequestMediaService

            const response = {
              data: await this.aiAgentApiService.getMediaItems(params, forceRefresh),
            };

            // Process the files to ensure they have all required properties for MediaObject
            try {
              if (response.data && response.data.files) {
                // Map each file to ensure it has all the expected properties
                response.data.files = response.data.files.map((file: any) => {
                  // Extract MIME type from file name if not provided
                  const getMimeType = (fileName: string): string => {
                    if (file.mime_type) return file.mime_type;

                    // Extract extension from filename
                    const extension = fileName.split('.').pop()?.toLowerCase();

                    // Map common extensions to MIME types
                    const mimeTypes: { [key: string]: string } = {
                      jpg: 'image/jpeg',
                      jpeg: 'image/jpeg',
                      png: 'image/png',
                      gif: 'image/gif',
                      svg: 'image/svg+xml',
                      webp: 'image/webp',
                      mp4: 'video/mp4',
                      webm: 'video/webm',
                      mov: 'video/quicktime',
                      avi: 'video/x-msvideo',
                      mp3: 'audio/mpeg',
                      wav: 'audio/wav',
                      ogg: 'audio/ogg',
                      m4a: 'audio/mp4',
                    };

                    return extension && mimeTypes[extension]
                      ? mimeTypes[extension]
                      : 'application/octet-stream';
                  };

                  // Determine the correct directory_id for the file
                  // If we're viewing a specific directory, files should be assigned to that directory
                  let fileDirectoryId = file.directory_id;
                  if (directoryId && (!fileDirectoryId || fileDirectoryId === '')) {
                    fileDirectoryId = directoryId;
                  }

                  // Create a MediaObject with all required properties
                  return {
                    id: file.id,
                    file_name: file.name || file.file_name || 'Untitled',
                    mime_type: getMimeType(file.name || file.file_name || ''),
                    size: file.size || 0,
                    directory_id: fileDirectoryId,
                    created_at: file.created_at || new Date().toISOString(),
                    updated_at: file.updated_at || file.created_at || new Date().toISOString(),
                    tags: file.tags || [],
                    collection_name: file.collection_name,
                    custom_properties: file.custom_properties || {},
                    index_url: file.url, // Set the URL from the API response
                    metadata: file.metadata || {},
                    // AI-generated analysis fields
                    generated_description: file.generated_description || '',
                    generated_tags: file.generated_tags || [],
                    analyze_state: file.analyze_state,
                    description: file.description || '',
                  };
                });
              }

              // Type guard to check if a value is a MediaResponse
              const isMediaResponseObject = (value: any): value is MediaResponse => {
                return (
                  value &&
                  typeof value === 'object' &&
                  !Array.isArray(value) &&
                  ('files' in value || 'folders' in value)
                );
              };

              // Filter by mime type if specified
              const mediaResponse: MediaResponse = isMediaResponseObject(response.data)
                ? response.data
                : {
                    folders: [],
                    files: response.data as MediaObject[],
                    currentPath: [], // Default path for array response format
                  };

              // Apply mime type filtering if requested
              if (mimeType && mimeType !== 'all') {
                mediaResponse.files = mediaResponse.files.filter((file: MediaObject) => {
                  return file.mime_type?.startsWith(mimeType);
                });
              }

              // Cache the successful response
              MediaService.cache.set(cacheKey, {
                data: mediaResponse,
                timestamp: Date.now(),
              });

              // Mark that we've completed the initial load
              if (!MediaService.initialLoadMade) {
                MediaService.initialLoadMade = true;
              }

              // Return the full response which contains files, folders, and currentPath
              resolve(mediaResponse);
            } catch (error: any) {
              console.error('Error fetching media items:', error);
              reject(error);
            } finally {
              // Clean up the pending request regardless of success or failure
              MediaService.pendingRequests.delete(requestKey);

              // Process any queued requests now that we're done with this one
              this.processQueuedRequests();
            }
          }
        }
      } catch (error) {
        console.error('Error fetching media items:', error);
        reject(error);
      }
    });

    MediaService.pendingRequests.set(requestKey, requestPromise);

    return requestPromise;
  }

  // Helper method to process queued media requests
  private processQueuedRequests(): void {
    // Release the lock first
    MediaService.mediaApiLocked = false;

    // Process the next request in the queue if there is one
    if (MediaService.mediaApiQueue.length > 0) {
      const nextRequest = MediaService.mediaApiQueue.shift()!;

      // Get current search query from store to ensure it's passed to queued requests
      const storeState = useMediaDirectoryStore.getState();
      const searchQuery = storeState.searchQuery;

      // Execute the request asynchronously to avoid blocking this method
      setTimeout(() => {
        try {
          // Handle different types of queued requests
          if (nextRequest.requestType === 'directories') {
            // This is a request for directory structure
            this.getDirectories().then(
              (data) => nextRequest.resolve(data),
              (error) => nextRequest.reject(error),
            );
          } else {
            // Default case - this is a media items request
            // Pass current search query to ensure consistent search behavior

            this.getMediaItems(
              nextRequest.directoryId,
              false, // Don't force refresh for queued requests
              nextRequest.fullPath,
              undefined, // Don't pass mime type - use store value
              searchQuery, // Pass current search query
            ).then(
              (data) => nextRequest.resolve(data),
              (error) => nextRequest.reject(error),
            );
          }
        } catch (error) {
          nextRequest.reject(error);
          // Continue processing the queue even if this request fails
          this.processQueuedRequests();
        }
      }, 0);
    } else {
    }
  }

  // Remove custom subdirectory fetching methods
  // We'll trust the API responses completely without any modifications

  // Get all directories with caching to prevent multiple API calls
  // priorityOperation=true means this call should bypass the normal locking mechanism
  // This is used for critical operations like moving files where we can't afford deadlocks
  async getDirectories(priorityOperation = false, brandId?: string): Promise<MediaDirectory[]> {
    // Create a unique cache key for this request
    const cacheKey = 'all-directories-structure';
    const requestKey = `get-${cacheKey}`;

    // Check if there's already a pending request for directories
    if (MediaService.pendingRequests.has(requestKey)) {
      return MediaService.pendingRequests.get(requestKey)!;
    }

    // Check if we have a recent cached directory structure
    if (
      MediaService.directoryStructureTimestamp > 0 &&
      Date.now() - MediaService.directoryStructureTimestamp < MediaService.DIRECTORY_CACHE_TTL
    ) {
      // We have a recent cached value, use it
      const cachedData = MediaService.cache.get(cacheKey);
      if (cachedData) {
        return cachedData.data as MediaDirectory[];
      }
    }

    // If we're already globally locked from another media API call, handle based on priority
    if (MediaService.mediaApiLocked) {
      // If this is a priority operation, bypass the lock
      if (priorityOperation) {
        // We'll proceed with the request below, but we won't actually set the lock ourselves
        // since that would interfere with the current operation that has the lock
      }
      // For normal operations, use the queue system
      else {
        const directoriesPromise = new Promise<MediaDirectory[]>((resolve, reject) => {
          MediaService.mediaApiQueue.push({
            resolve,
            reject,
            requestType: 'directories',
          });
        });

        // Store this promise so other directory requests can reuse it
        MediaService.pendingRequests.set(requestKey, directoriesPromise);
        return directoriesPromise;
      }
    }
    // We're not locked, so we can proceed normally
    else {
      // For non-priority operations, acquire the global lock
      if (!priorityOperation) {
        MediaService.mediaApiLocked = true;
      } else {
      }
    }

    // Create the directory structure request promise
    const directoryStructurePromise = new Promise<MediaDirectory[]>(async (resolve, reject) => {
      try {
        // Get agent ID before making the request
        const agent_id = await this.getAgentId();

        // Make the API request to fetch the root folders first
        const response = await axios.get(`${this.beApiUrl}/ai-agent/media`, {
          headers: this.headers,
          params: {
            agent_id,
            // CRITICAL: Add brand ID as user_id parameter for brand-specific filtering
            ...(brandId ? { user_id: brandId } : {}),
          },
        });

        // Extract and log the folders data
        const mediaResponse = response.data as MediaResponse;

        // Extract the root directory names from the API response
        const rootFolders = mediaResponse.folders || [];

        // Create a map to store all directory information
        const folderMap = new Map<string, MediaDirectory>();

        // Create a map to track parent-child relationships for subdirectories
        const subfolderParentMap = new Map<string, string>();

        // Create MediaDirectory objects for each root folder
        rootFolders.forEach((folderName) => {
          // Root directory IDs are prefixed with 'root:' to avoid collisions with subdirectories
          const directoryId = `root:${folderName}`;

          const directoryObj: MediaDirectory = {
            id: directoryId, // Create a unique ID format for root directories
            name: folderName,
            parent_id: null, // Root folders have no parent
            parent_directory_id: null, // Match the parent_id for consistency
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          // Store with both the ID and the name for backward compatibility
          folderMap.set(directoryId, directoryObj);
          // Also store under the plain name for backward compatibility
          // but mark this as the authoritative entry
          folderMap.set(folderName, directoryObj);
        });

        // For each root directory, fetch its subdirectories
        for (const rootFolder of rootFolders) {
          await this.fetchAndMapSubdirectories(rootFolder, agent_id, folderMap, subfolderParentMap);
        }

        // IMPORTANT: Process the subdirectory relationships
        // We need to make sure subdirectories from the API are properly associated with their parent directories
        // This ensures subdirectories like "ppp" and "ooo" are properly shown under "ttt" if they belong there
        // but were returned at the root level by the API

        // First process the parent-child relationships we've explicitly recorded
        for (const [childId, parentId] of subfolderParentMap.entries()) {
          // Get the child and parent directory objects
          const childDir = folderMap.get(childId);
          const parentDir = folderMap.get(parentId);

          // If both exist, ensure the child has the correct parent_id
          if (childDir && parentDir) {
            // Update the parent relationship
            childDir.parent_id = parentId;
            childDir.parent_directory_id = parentId;
          }
        }

        // For API responses that incorrectly show subdirectories at the root level
        // This specifically addresses the case where "ppp" and "ooo" should be subdirectories of "ttt"
        // but the API returned them as root directories

        // Get any media responses from cache where we have subdirectory information
        for (const [key, value] of MediaService.cache.entries()) {
          // Look for cached responses for specific directories
          if (key.startsWith('media-items-') && key !== 'media-items-root') {
            try {
              const cachedDirectoryData = value.data as MediaResponse;

              // Check if this is a valid directory response with a currentPath
              if (
                cachedDirectoryData &&
                cachedDirectoryData.currentPath &&
                cachedDirectoryData.currentPath.length > 0 &&
                cachedDirectoryData.folders &&
                cachedDirectoryData.folders.length > 0
              ) {
                // Get the current directory this cached response is for
                const currentDir =
                  cachedDirectoryData.currentPath[cachedDirectoryData.currentPath.length - 1];

                // Get subdirectories from this response
                const subdirs = cachedDirectoryData.folders;

                // Update the folder map with these explicit parent-child relationships
                for (const subdir of subdirs) {
                  // Check if this subdirectory exists in our map
                  let subdirObj = folderMap.get(subdir);

                  // If it exists, make sure it has the correct parent
                  if (subdirObj) {
                    // If it was incorrectly identified as a root directory or has a different parent,
                    // update its parent to the correct one
                    if (!subdirObj.parent_id || subdirObj.parent_id !== currentDir) {
                      subdirObj.parent_id = currentDir;
                      subdirObj.parent_directory_id = currentDir;
                    }
                  }
                  // If it doesn't exist yet, create it
                  else {
                    subdirObj = {
                      id: subdir,
                      name: subdir,
                      parent_id: currentDir,
                      parent_directory_id: currentDir,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    };
                    folderMap.set(subdir, subdirObj);
                  }
                }
              }
            } catch (err) {
              console.error(`Error processing cached directory data for ${key}:`, err);
            }
          }
        }

        // Create the final array of all directories with proper parent-child hierarchy
        const allDirectories = Array.from(folderMap.values());

        // Build full paths for all directories
        // This is essential for the moveMedia function to work correctly
        const buildFullPath = (dir: MediaDirectory): string[] => {
          const path: string[] = [];
          let currentDir: MediaDirectory | undefined = dir;

          // Traverse up the parent chain to build the complete path
          while (currentDir) {
            // Add current directory name to the beginning of the path
            path.unshift(currentDir.name);

            // If no parent, we're at the root level
            if (!currentDir.parent_id) break;

            // Find the parent directory
            currentDir = allDirectories.find((d) => d.id === currentDir!.parent_id);

            // Safety check to prevent infinite loops
            if (!currentDir) {
              Logger.warn(
                `Parent directory ${dir.parent_id} not found while building path`,
                'MediaService',
              );
              break;
            }
          }

          return path;
        };

        // Populate the fullPath for all directories
        allDirectories.forEach((dir: MediaDirectory) => {
          const fullPath = buildFullPath(dir);

          // Add or update custom_properties with the fullPath
          dir.custom_properties = {
            ...(dir.custom_properties || {}),
            fullPath: fullPath,
          };

          if (dir.parent_id) {
            Logger.debug(
              `Directory "${dir.name}" fullPath: ${fullPath.join(' -> ')}`,
              'MediaService',
            );
          } else {
            Logger.debug(
              `Root directory "${dir.name}" fullPath: ${fullPath.join(' -> ')}`,
              'MediaService',
            );
          }
        });

        // Cache the successful result
        MediaService.cache.set(cacheKey, {
          data: allDirectories,
          timestamp: Date.now(),
        });

        // Update the timestamp of our last directory structure fetch
        MediaService.directoryStructureTimestamp = Date.now();

        resolve(allDirectories);
      } catch (error) {
        console.error('Error fetching directories:', error);
        reject(error);
      } finally {
        // Clean up the pending request regardless of success or failure
        MediaService.pendingRequests.delete(requestKey);

        // Process any queued requests now that we're done
        this.processQueuedRequests();
      }
    });

    // Store the promise for reuse
    MediaService.pendingRequests.set(requestKey, directoryStructurePromise);
    return directoryStructurePromise;
  }

  private async fetchAndMapSubdirectories(
    path: string,
    agentId: string,
    folderMap: Map<string, MediaDirectory>,
    subfolderParentMap: Map<string, string>,
    brandId?: string, // Brand ID to pass as user_id parameter
  ): Promise<void> {
    try {
      // Create a unique cache key for this subdirectory request
      const cacheKey = `subdirectory-${path}`;
      const requestKey = `get-${cacheKey}`;

      // Check if there's a pending request for this subdirectory
      let response;
      if (MediaService.pendingRequests.has(requestKey)) {
        response = { data: await MediaService.pendingRequests.get(requestKey)! };
      }
      // Check if we have this subdirectory in cache
      else {
        const cachedData = MediaService.cache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp < this.CACHE_TTL) {
          response = { data: cachedData.data };
        }
        // Make the API request if not cached
        else {
          // Create a promise for this request that can be reused by identical calls
          const subdirPromise = new Promise(async (resolve, reject) => {
            try {
              // For each directory, we need to query the API with the path parameter
              // We'll use the shared locking mechanism for this
              if (MediaService.mediaApiLocked) {
                // Wait for the lock to be released through our queue system
                await new Promise<void>((innerResolve) => {
                  MediaService.mediaApiQueue.push({
                    resolve: () => innerResolve(),
                    reject: () => innerResolve(), // Still resolve so we can continue
                    directoryId: path,
                    requestType: 'subdirectory',
                  });
                });
              }

              // We can now make the API call - no need to acquire the lock since this
              // happens within the context of the main directories request which already has the lock

              const apiResponse = await axios.get(`${this.beApiUrl}/ai-agent/media`, {
                headers: this.headers,
                params: {
                  agent_id: agentId,
                  path: path, // Now sending path as string
                  // CRITICAL: Add brand ID as user_id parameter for brand-specific filtering
                  ...(brandId ? { user_id: brandId } : {}),
                },
              });

              // Cache the result
              MediaService.cache.set(cacheKey, {
                data: apiResponse.data,
                timestamp: Date.now(),
              });

              resolve(apiResponse.data);
            } catch (error) {
              console.error(`Error fetching subdirectory ${path}:`, error);
              reject(error);
            } finally {
              // Remove from pending requests
              MediaService.pendingRequests.delete(requestKey);
            }
          });

          // Store the promise for reuse
          MediaService.pendingRequests.set(requestKey, subdirPromise);

          // Wait for the response
          response = { data: await subdirPromise };
        }
      }

      // Get any subdirectories
      const subfolders = response.data.folders || [];

      // Find the parent directory ID
      let parentDirId = path;
      // If path is a folder name, try to find its ID
      if (path.indexOf('/') === -1 && !path.startsWith('root:') && !path.startsWith('subdir:')) {
        // Try to find if this is a root directory
        const rootDirId = `root:${path}`;
        if (folderMap.has(rootDirId)) {
          parentDirId = rootDirId;
        }
      }

      // Add each subfolder to our directories map
      for (const subfolder of subfolders) {
        // IMPORTANT: Create a unique ID for each subdirectory that includes its full path
        // This ensures directories with the same name at different levels don't collide

        // For subdirectories, we need to ensure we have the right path and parent relationship
        // The full path string will be like "parent/child"
        const pathForRequests = `${path}/${subfolder}`;

        // Create a unique ID that encodes the full path information
        // Format: "subdir:parent/child" - this ensures uniqueness across the directory tree
        const uniqueSubfolderId = `subdir:${pathForRequests}`;

        // Store the parent relationship explicitly to ensure UI can render correctly
        // Use the unique ID for the child but keep the simple name or ID for the parent
        subfolderParentMap.set(uniqueSubfolderId, parentDirId);

        // Create subfolder object with explicit parent references
        // CRITICAL: Make sure parent_id and parent_directory_id are set to the PARENT directory ID
        // and include proper API path information in custom_properties
        const apiPathSegments = pathForRequests.split('/');
        const subfolderObj: MediaDirectory = {
          id: uniqueSubfolderId, // Primary ID is the unique path-based ID
          name: subfolder, // The display name is just the directory name
          parent_id: parentDirId, // Set parent ID to parent directory
          parent_directory_id: parentDirId, // Also set parent_directory_id for DirectoryItem filtering
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          custom_properties: {
            // Store the full API path for proper rendering in the directory tree
            apiPath: pathForRequests,
            // Also store path as array for compatibility with other code
            path: apiPathSegments,
          },
        };

        // Log the folder relationships for debugging

        // Add to our map with the unique ID
        folderMap.set(uniqueSubfolderId, subfolderObj); // Store with unique ID as primary key

        // Also add with the simple name, but only if there isn't already a directory with this name
        // This prevents conflicts when multiple directories have the same name in different paths
        if (!folderMap.has(subfolder)) {
          folderMap.set(subfolder, subfolderObj); // Also store with simple name for lookups
        }

        // Recursively fetch further subdirectories for this subfolder
        // Pass the full path for nested subdirectory requests
        await this.fetchAndMapSubdirectories(
          pathForRequests,
          agentId,
          folderMap,
          subfolderParentMap,
        );
      }
    } catch (error) {
      console.error(`Error fetching subdirectories for ${path}:`, error);
    }
  }

  // Helper function to parse JWT token and extract info
  private parseJwtToken(token: string): { url: string; bucket: string; objectPath: string } {
    try {
      // The token is a JWT with three parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT token format');
      }

      // Add padding to the base64 string if needed
      const padded =
        parts[1].replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((parts[1].length + 3) % 4);

      // Decode the payload
      const payload = JSON.parse(atob(padded));

      if (!payload.url) {
        throw new Error('Token payload does not contain url');
      }

      // The url field contains the bucket/path format
      const url = payload.url;
      const pathParts = url.split('/');
      const bucket = pathParts[0];
      const objectPath = pathParts.slice(1).join('/');

      return { url, bucket, objectPath };
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      throw new Error('Failed to parse upload token');
    }
  }

  // Delete a media item
  async deleteMedia(mediaId: string): Promise<boolean> {
    try {
      // Get agent ID before making the request
      const agent_id = await this.getAgentId();

      await axios.delete(`${this.beApiUrl}/ai-agent/media/${mediaId}`, {
        headers: this.headers,
        params: { agent_id },
      });

      return true;
    } catch (error: any) {
      Logger.error('Error deleting media', 'MediaService', error);

      // Preserve backend error information for frontend handling
      if (error.response) {
        error.response = error.response;
      }

      throw error;
    }
  }

  // Get a single media item by ID (fresh data from the server)
  async getMediaItemById(
    mediaId: string,
    forceRefresh = false,
    timestamp?: number,
    brandIdOverride?: string,
  ): Promise<MediaObject> {
    try {
      // Add cache-busting parameters to ensure we get fresh data when requested
      const params: Record<string, string> = {};

      // Use either provided timestamp or generate a new one
      const cacheTimestamp = timestamp || Date.now();

      if (forceRefresh) {
        params['_cache'] = cacheTimestamp.toString();
        // Add additional cache-busting parameters to be extra sure
        params['_t'] = `${cacheTimestamp}_${Math.random().toString(36).substring(2, 9)}`;
      }

      // Build headers with optional brandId override for x-brand-id
      // This is needed for WebView onboarding where we skip setSelectedBrand
      const requestHeaders = { ...this.headers };
      if (brandIdOverride) {
        requestHeaders['x-brand-id'] = brandIdOverride;
      }

      const response = await axios.get(`${this.beApiUrl}/ai-agent/media/${mediaId}`, {
        headers: requestHeaders,
        params: params,
      });

      return response.data;
    } catch (error) {
      console.error(`Error fetching media item with ID ${mediaId}:`, error);
      throw error;
    }
  }

  /**
   * Update tags for a media file
   * @param mediaId The ID of the media file
   * @param tags Array of tags to set or a string that can be parsed into an array
   * @returns Promise with the updated media object
   */
  async updateMediaTags(mediaId: string, tags: string[] | string): Promise<MediaObject> {
    try {
      // First get the current media object to preserve existing metadata
      const currentMedia = await this.getMediaItemById(mediaId, true);

      // Keep all existing metadata and just update the tags field
      const currentMetadata = currentMedia.metadata || {};

      // Ensure tags is always an array before sending to the API
      if (!Array.isArray(tags)) {
        // If tags is a string that looks like JSON, try to parse it
        if (typeof tags === 'string' && tags.startsWith('[') && tags.endsWith(']')) {
          try {
            tags = JSON.parse(tags);
          } catch (e) {
            // If parsing fails, default to empty array
            tags = [];
          }
        } else {
          // If not an array or parseable JSON, convert to array with single item or empty array
          tags = typeof tags === 'string' && tags.trim() ? [tags] : [];
        }
      }

      // Create a payload that preserves all existing metadata fields
      const payload = {
        metadata: {
          ...currentMetadata, // Preserve all existing metadata fields
          tags: tags, // Send tags directly as an array as expected by the API
        },
      };

      // Make the PATCH request with the properly formatted payload
      const response = await axios.patch(`${this.beApiUrl}/ai-agent/media/${mediaId}`, payload, {
        headers: this.headers,
      });

      // Return the updated media object
      if (response.data) {
        return response.data;
      }

      // If no response data, update our local version
      return {
        ...currentMedia,
        metadata: {
          ...currentMetadata,
          tags: tags, // Keep tags as an array in local state
        },
        updated_at: new Date().toISOString(),
      };
    } catch (error: any) {
      console.error('Error updating media tags:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  }

  /**
   * Rename a media file
   * @param mediaId The ID of the media file to rename
   * @param newFileName The new file name
   * @returns Promise with the updated media object
   */
  async renameMediaFile(mediaId: string, newFileName: string): Promise<MediaObject> {
    try {
      // Make the PATCH request to update just the file name
      const response = await axios.patch(
        `${this.beApiUrl}/ai-agent/media/${mediaId}`,
        {
          file_name: newFileName,
        },
        {
          headers: this.headers,
        },
      );

      // The API should return the updated media object
      if (response.data) {
        return response.data;
      }

      // Create a default response if the API doesn't return the media object
      const defaultResponse: MediaObject = {
        id: mediaId,
        file_name: newFileName,
        mime_type: '', // Will get replaced when refreshing
        size: 0, // Will get replaced when refreshing
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      return defaultResponse;
    } catch (error: any) {
      console.error('Error renaming media file:', error);
      // Add more detailed error information for debugging
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  }

  /**
   * Update media metadata using the AI Agents API
   * This method is primarily used for the AI Agents backend
   */
  async updateMediaMetadata(mediaId: string, metadata: any): Promise<MediaObject> {
    try {
      // Structure the request according to what the backend expects in MediaUpdateDto
      // IMPORTANT: Do NOT include agent_id in this request as it's not part of the DTO

      // Handle the case where metadata is sent in a flat structure (from media-item-detail.tsx)
      // or in a nested structure (from other components)
      let tags = metadata.tags || [];
      let author = metadata.author || '';
      let location = metadata.location || '';
      let copyright = metadata.copyright || '';
      const rawDescription = metadata.description;
      // Send empty string to clear description (main backend DTO now passes it through)
      const description = rawDescription ?? '';

      // If metadata object had nested metadata, use those values directly
      if (metadata.metadata) {
        if (metadata.metadata.tags) tags = metadata.metadata.tags;
        if (metadata.metadata.author) author = metadata.metadata.author;
        if (metadata.metadata.location) location = metadata.metadata.location;
        if (metadata.metadata.copyright) copyright = metadata.metadata.copyright;
      }

      // Ensure tags is always an array before sending to the API
      if (!Array.isArray(tags)) {
        // If tags is a string that looks like JSON, try to parse it
        if (typeof tags === 'string' && tags.startsWith('[') && tags.endsWith(']')) {
          try {
            tags = JSON.parse(tags);
          } catch (e) {
            // If parsing fails, default to empty array
            tags = [];
          }
        } else {
          // If not an array or parseable JSON, convert to array with single item or empty array
          tags = typeof tags === 'string' && tags.trim() ? [tags] : [];
        }
      }

      // The backend expects metadata as a Record<string, string>, where all values must be strings

      // Create a metadata object with string values
      const metadataRecord: Record<string, any> = {
        // Always ensure tags is an array in the request (not a string)
        tags: tags,
        author: author || '',
        location: location || '',
        copyright: copyright || '',
      };

      // Add any custom properties
      if (metadata.customProperties) {
        Object.entries(metadata.customProperties).forEach(([key, value]) => {
          metadataRecord[key] = typeof value === 'string' ? value : JSON.stringify(value);
        });
      }

      const requestData: { metadata: Record<string, string>; description?: string } = {
        metadata: metadataRecord,
        ...(rawDescription !== undefined && { description }),
      };

      // Make the API call to AI Agent API
      const response = await axios.patch(
        `${this.beApiUrl}/ai-agent/media/${mediaId}`,
        requestData,
        {
          headers: this.headers,
        },
      );

      // Check if we have response data to work with
      if (response.data && typeof response.data === 'object') {
        // Convert the response data into a properly formatted media object
        // that can be used directly in the UI
        const updatedMedia = {
          ...response.data,
          ...(rawDescription !== undefined ? { description: description } : {}),
          ...(rawDescription !== undefined ? { comment: description } : {}),
          // Ensure metadata is properly structured for the UI components
          metadata: response.data.metadata || metadataRecord,
          // Make sure we include the updated values in custom_properties as well for UI consistency
          custom_properties: {
            ...(response.data.custom_properties || {}),
            ...(rawDescription !== undefined ? { description: description } : {}),
            author: author || response.data.metadata?.author || '',
            location: location || response.data.metadata?.location || '',
            copyright: copyright || response.data.metadata?.copyright || '',
          },
        };

        return updatedMedia;
      }

      return response.data;
    } catch (error) {
      console.error('Error updating media metadata:', error);
      // Add more detailed error information for debugging
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  }

  // Store for pending files that haven't been fully uploaded yet
  private pendingMetadataUpdates: Record<string, any> = {};

  /**
   * Track a completed upload and apply any pending metadata updates
   * Call this after an upload successfully completes
   */
  trackCompletedUpload(tempId: string, finalMediaObject: MediaObject): void {
    // Check if we have pending metadata for this upload
    const pendingUpdate =
      this.pendingMetadataUpdates[tempId] || this.pendingMetadataUpdates['pending'];

    if (pendingUpdate) {
      // Apply the metadata update asynchronously
      setTimeout(() => {
        this.updateMediaMetadata(finalMediaObject.id, pendingUpdate.metadata)
          .then(() => {
            // Clear the pending update
            delete this.pendingMetadataUpdates[tempId];
            if (tempId !== 'pending') {
              delete this.pendingMetadataUpdates['pending'];
            }
          })
          .catch((err) => {
            console.error('Failed to apply pending metadata:', err);
          });
      }, 1000); // Small delay to ensure the file is fully processed on the server
    }
  }

  /**
   * Update ALL versions of a file across all APIs and systems
   * This is a robust solution that ensures metadata is synchronized everywhere
   */
  async updateAllMetadataVersions(mediaId: string, metadata: any): Promise<MediaObject> {
    // Special handling for files that are still being uploaded (have 'pending' ID)
    if (mediaId === 'pending') {
      // Store metadata to be applied later when the real ID is available
      this.pendingMetadataUpdates['pending'] = {
        metadata,
        timestamp: Date.now(),
      };

      // Return a mock success response to avoid breaking the UI flow
      return {
        id: 'pending',
        file_name: metadata.fileName || 'Uploading file',
        mime_type: metadata.mimeType || 'application/octet-stream',
        size: 0,
        collection_name: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        // Include the metadata that was attempted to be saved
        metadata: metadata,
        custom_properties: {
          description: metadata.description,
          tags: metadata.tags || [],
          author: metadata.author,
          location: metadata.location,
          copyright: metadata.copyright,
        },
      } as MediaObject;
    }

    // 1. Get current file data to ensure we have accurate information
    let mediaObject: MediaObject;
    try {
      mediaObject = await this.getMediaItemById(mediaId);
    } catch (error) {
      console.error('Failed to fetch current media data:', error);
      // If we can't get the current data, proceed with what we have
      mediaObject = { id: mediaId } as MediaObject;
    }

    // 2. Search for all instances of this file across our system
    // Find all files with this filename to get all possible IDs
    let matchingItems: MediaObject[] = [];
    try {
      const allMediaItems = await this.getMediaItems();

      // Find matches based on filename
      matchingItems = allMediaItems.files.filter(
        (item) => item.file_name === mediaObject.file_name,
      );
    } catch (error) {
      console.error('Error finding matching items:', error);
      // Continue with just the original ID
    }

    // 3. Try to update using the original ID first
    let result = mediaObject;
    let originalUpdateSuccess = false;
    let originalError: any = null;

    try {
      result = await this.updateMediaMetadata(mediaId, metadata);
      originalUpdateSuccess = true;
    } catch (error) {
      console.error('Failed to update with original ID:', error);
      originalError = error; // Store the error for later re-throwing
    }

    // 4. If we found matching items, try to update those too
    // Skip the ID we already successfully updated
    if (matchingItems.length > 0) {
      for (const item of matchingItems) {
        // Skip the ID we already updated successfully
        if (originalUpdateSuccess && item.id === mediaId) {
          continue;
        }

        try {
          // Only try to update secondary IDs if the primary update succeeded
          // to avoid inconsistent states
          if (originalUpdateSuccess) {
            await this.updateMediaMetadata(item.id, metadata);
          }
        } catch (itemError) {
          console.error(`Failed to update ID ${item.id}:`, itemError);
          // Don't fail the overall operation for secondary IDs
        }
      }
    }

    // If the original update failed, rethrow the error to notify the UI
    if (!originalUpdateSuccess && originalError) {
      throw originalError;
    }

    return result;
  }

  // Move media to a different directory
  async moveMedia(
    mediaId: string,
    targetDirectoryId: string | string[] | null,
  ): Promise<MediaObject> {
    try {
      // Get agent ID before making the request
      const agent_id = await this.getAgentId();

      // Handle different formats of targetDirectoryId
      // 1. null or 'none' = root directory (empty path)
      // 2. string[] = explicit directory path (['ttt', 'ppp', 'opa'])
      // 3. string = directory ID that needs to be converted to a path
      let normalizedTargetDir: string | null = null;

      // Prepare the destination path
      let destinationPath: string[] = [];

      // Handle array case - already in correct format for API
      if (Array.isArray(targetDirectoryId)) {
        destinationPath = targetDirectoryId;
      }
      // Handle string case - need to convert to path format
      else if (typeof targetDirectoryId === 'string' && targetDirectoryId !== 'none') {
        normalizedTargetDir = targetDirectoryId;
      }
      // Handle null or 'none' - root directory
      else {
        // Empty destination path = root directory
        // Backend now properly handles empty arrays for root directory moves
        destinationPath = [];
      }

      // Check if we're in a locked state before proceeding
      const wasLocked = MediaService.mediaApiLocked;
      if (wasLocked) {
        // Keep track of the queue so we can restore it after our priority operation
        const originalQueue = [...MediaService.mediaApiQueue];

        // Temporarily release the lock to allow our critical move operation
        MediaService.mediaApiLocked = false;
        MediaService.mediaApiQueue = [];

        // Schedule queue restoration after our operation
        setTimeout(() => {
          MediaService.mediaApiQueue = [...originalQueue];
          if (originalQueue.length > 0) {
            this.processQueuedRequests();
          }
        }, 1000); // Delay restoration to ensure our operation completes
      }

      // Get directory information - safely handle the directory structure checking
      if (normalizedTargetDir) {
        try {
          // Create a function to safely get directories without causing deadlocks
          const getDirectoriesSafely = async (): Promise<MediaDirectory[]> => {
            try {
              // First check if we have a cached structure we can use
              const cacheKey = 'all-directories-structure';
              const cachedData = MediaService.cache.get(cacheKey);

              if (cachedData && Date.now() - cachedData.timestamp < this.CACHE_TTL) {
                return cachedData.data as MediaDirectory[];
              }

              // If no cache is available, make a direct API call with a temporary release of locks

              return await this.getDirectories(true); // Use priority flag to bypass locks
            } catch (dirError) {
              console.warn('Unable to get directory structure, will use simple path:', dirError);
              return [];
            }
          };

          // Try to get directory information without causing deadlocks
          const allDirectories = await getDirectoriesSafely();

          if (allDirectories.length > 0) {
            // The targetDir might be using our new unique ID format (root: or subdir:) or the simple name
            // We need to handle both cases
            let targetDir = allDirectories.find((d) => d.id === normalizedTargetDir);

            // If not found directly, try to find by matching any ID that ends with the name
            // This handles cases where normalizedTargetDir is a simple name but stored with a prefix
            if (!targetDir) {
              targetDir = allDirectories.find(
                (d) =>
                  (d.id.startsWith('root:') && d.id.substring(5) === normalizedTargetDir) ||
                  (d.id.startsWith('subdir:') && d.id.endsWith(`/${normalizedTargetDir}`)) ||
                  d.name === normalizedTargetDir,
              );
            }

            // Use the stored fullPath from custom_properties if available
            // This matches how drag-and-drop works and ensures we get the complete path
            if (targetDir) {
              // First try to use the pre-built fullPath from custom_properties
              if (
                targetDir.custom_properties?.fullPath &&
                Array.isArray(targetDir.custom_properties.fullPath)
              ) {
                destinationPath = targetDir.custom_properties.fullPath;
                Logger.info(
                  `Using stored fullPath: ${destinationPath.join(' -> ')}`,
                  'MediaService',
                );
              }
              // Fallback: Try to use the path property
              else if (Array.isArray((targetDir as any).path)) {
                destinationPath = (targetDir as any).path;
                Logger.info(`Using stored path: ${destinationPath.join(' -> ')}`, 'MediaService');
              }
              // Last resort: Use just the directory name
              else {
                destinationPath = [targetDir.name];
                Logger.warn(
                  `No fullPath or path available, using simple name: ${targetDir.name}`,
                  'MediaService',
                );
              }
            } else {
              // Directory not found at all - use the ID as fallback
              destinationPath = [normalizedTargetDir];
              Logger.warn(
                `Directory not found in structure, using ID as fallback: ${normalizedTargetDir}`,
                'MediaService',
              );
            }
          } else {
            // No directory information available - use simple path
            destinationPath = [normalizedTargetDir];
            Logger.warn(
              'No directory structure information available, using simple path',
              'MediaService',
            );
          }
        } catch (error) {
          Logger.warn(
            'Error determining subdirectory path, using simple path',
            'MediaService',
            error,
          );
          // Fallback if directory structure lookup fails - use the ID directly
          destinationPath = [normalizedTargetDir];
        }
      }

      // Request body with destination_path format
      const requestBody = {
        destination_path: destinationPath,
        agent_id,
      };

      // Make sure we have a valid media ID
      if (!mediaId || mediaId === 'pending') {
        throw new Error('Cannot move a file with an invalid or pending ID');
      }

      // Send the request to the regular media update endpoint
      // NOTE: There is no dedicated /move endpoint, we use the standard update endpoint

      const response = await axios.patch(
        `${this.beApiUrl}/ai-agent/media/${mediaId}`,
        requestBody,
        {
          headers: this.headers,
        },
      );

      // Consider the operation successful if we got a 200 response
      // Since we're using destination_path, the validation is different
      const moveSuccessful = response.status === 200 || response.status === 201;

      // Log the result for debugging

      // For backward compatibility, check if there's still a directory_id property
      // and match it against what we expected
      const responseDirectoryId = response.data.directory_id;
      const expectedDirectoryId = normalizedTargetDir;

      // For backward compatibility with the existing code
      const directoryChanged = moveSuccessful;

      if (!directoryChanged) {
        console.warn('API returned success but directory_id may not be updated correctly!', {
          requestedDirectory: expectedDirectoryId,
          actualDirectory: responseDirectoryId,
        });
      } else {
      }

      return response.data;
    } catch (error) {
      console.error('\u274c Error moving media:', error);
      // Provide more detailed error information for debugging
      if (axios.isAxiosError(error) && error.response) {
        console.error('API Error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  /**
   * Move ALL versions of a file across all APIs and systems
   * This is a robust solution that ensures directory changes are synchronized everywhere,
   * similar to how updateAllMetadataVersions works for metadata updates.
   *
   * This method uses the destination_path approach to move files between directories.
   */
  async moveAllMediaVersions(
    mediaId: string,
    targetDirectoryId: string | null,
  ): Promise<MediaObject> {
    // 1. Get current file data to ensure we have accurate information
    let mediaObject: MediaObject;
    try {
      mediaObject = await this.getMediaItemById(mediaId);
    } catch (error) {
      console.error('Failed to fetch current media data:', error);
      // If we can't get the current data, proceed with what we have
      mediaObject = { id: mediaId } as MediaObject;
    }

    // Set up variables to track success
    let result: MediaObject = mediaObject;
    let aiAgentApiSuccess = false;
    let coreApiSuccess = false;

    // 2. Use the main AI Agent API with destination_path format
    try {
      // This will now use the destination_path format underneath
      result = await this.moveMedia(mediaId, targetDirectoryId);
      aiAgentApiSuccess = true;
    } catch (aiError) {
      console.error('Failed to move media in AI Agent API:', aiError);
    }

    // 3. Skip Core API update since that endpoint doesn't seem to exist
    // Instead, rely solely on the AI-agent API which is the primary endpoint
    try {
      // If AI agent API succeeded, consider the overall operation successful
      if (aiAgentApiSuccess) {
        coreApiSuccess = true; // Mark as successful since we're not using Core API
      }
    } catch (coreError) {
      console.error('Error in secondary move operation:', coreError);
    }

    // Return the original result from the first successful API call
    if (!aiAgentApiSuccess && !coreApiSuccess) {
      throw new Error('Failed to move media in both backend systems');
    }

    return result;
  }

  // Create a new directory
  async createDirectory(
    name: string,
    parentDirectoryId: string | null = null,
  ): Promise<MediaDirectory> {
    try {
      // Get agent ID before making the request
      const agent_id = await this.getAgentId();

      // The path field is required by the API for folder creation
      let parent_id: string | null = parentDirectoryId;

      // If we have a parent directory ID, convert it to the API path format
      if (parentDirectoryId) {
        // Convert internal ID to proper API path if needed
        if (parentDirectoryId.startsWith('dir-root-')) {
          // For root directories, just use the name (dir-root-X => X)
          parent_id = parentDirectoryId.substring('dir-root-'.length);
        } else if (parentDirectoryId.includes('/')) {
          // Handle nested directory IDs with path structure
          // Format: dir-child-parent/child/grandchild => parent/child/grandchild
          const pathParts = parentDirectoryId.split('/');

          // For dir-child-X/Y/Z... format, extract all path segments
          if (pathParts[0].startsWith('dir-child-')) {
            const parentName = pathParts[0].substring('dir-child-'.length);
            const remainingParts = pathParts.slice(1); // Get all parts after the first one
            parent_id = [parentName, ...remainingParts].join('/');
          } else {
            // If not in the expected format, use the raw path segments
            parent_id = parentDirectoryId;
          }
        } else if (parentDirectoryId.startsWith('dir-child-')) {
          // Generic pattern for child directories: dir-child-X => X
          // This removes the prefix but preserves the actual directory name
          parent_id = parentDirectoryId.substring('dir-child-'.length);
        } else {
          // Try to look up the directory in our cache as a fallback
          const cachedDir = this.findDirectoryById(parentDirectoryId);
          if (cachedDir && cachedDir.custom_properties?.apiPath) {
            parent_id = cachedDir.custom_properties.apiPath;
          }
        }
      }

      // Construct the path array required by the API
      let pathArray: string[] = [];

      if (parent_id) {
        const parentSegments = parent_id.split('/');
        // Add all parent segments plus the new directory name
        pathArray = [...parentSegments, name];
      } else {
        // Root-level directory just has its own name in the path
        pathArray = [name];
      }

      if (pathArray.length >= 6) {
        throw new Error('Directory path cannot exceed 5 levels');
      }

      // Create the API request payload
      const requestPayload = {
        name,
        parent_id,
        agent_id,
        path: pathArray, // The API requires this path field
      };

      // Make the API request
      const response = await axios.post(`${this.beApiUrl}/ai-agent/media/folders`, requestPayload, {
        headers: this.headers,
      });

      console.log('pathArray', pathArray);
      console.log('parent_id', parent_id);
      console.log('name', name);
      console.log('agent_id', agent_id);
      console.log('response', response);

      // Map the response to our MediaDirectory format
      const createdDirectory: MediaDirectory = {
        id: response.data.id || `dir-${Date.now()}`,
        name: response.data.name || name,
        parent_directory_id: parentDirectoryId,
        created_at: response.data.created_at || new Date().toISOString(),
        updated_at: response.data.updated_at || new Date().toISOString(),
        // Store the API path for future use
        custom_properties: {
          apiPath: response.data.path
            ? Array.isArray(response.data.path)
              ? response.data.path.join('/')
              : response.data.path
            : parent_id
              ? `${parent_id}/${name}`
              : name,
        },
      };

      // After creating a directory, refresh the directory tree
      setTimeout(() => {
        this.getDirectoryTree().catch((err) =>
          console.error('Error refreshing directory tree after creation:', err),
        );
      }, 500);

      return createdDirectory;
    } catch (error) {
      console.error('Error creating directory:', error);
      throw error;
    }
  }

  // Rename a directory
  async renameDirectory(
    directoryId: string,
    newName: string,
  ): Promise<MediaDirectory & { success: boolean }> {
    try {
      // Get agent ID before making the request
      const agent_id = await this.getAgentId();

      // Get the directory from the cached directory tree
      const directory = this.findDirectoryById(directoryId);
      if (!directory) {
        console.warn(`Directory with ID ${directoryId} not found in cache`);
      }

      // Get the store state for path information
      const store = useMediaDirectoryStore.getState();
      let pathArray: string[] = [];

      // CASE 1: If this is the current directory, use the current path
      if (directoryId === store.currentDirectoryId && store.apiPath) {
        // Use the current path segments
        pathArray = store.apiPath.split('/').filter((s) => s.trim() !== '');
      }
      // CASE 2: If we have the directory in cache, use its info
      else if (directory) {
        // First, add the current directory name
        const currentDirName = directory.name;

        // Check if this is a subdirectory (has a parent)
        if (directory.parent_directory_id || directory.parent_id) {
          // Get the parent directory
          const parentId = directory.parent_directory_id || directory.parent_id;
          if (parentId) {
            const parentDir = this.findDirectoryById(parentId);
            if (parentDir) {
              // We have found the parent directory, use its name and the current directory name
              pathArray = [parentDir.name, currentDirName];
            } else {
              pathArray = [currentDirName];
            }
          } else {
            pathArray = [currentDirName];
          }
        } else {
          // This is a top-level directory
          pathArray = [currentDirName];
        }
      }
      // CASE 3: Try to get the path from the directory path map
      else if (store.directoryPathMap && store.directoryPathMap[directoryId]) {
        pathArray = store.directoryPathMap[directoryId].split('/').filter((s) => s.trim() !== '');
      }
      // CASE 4: Try to find the directory in the cached directories
      else {
        const currentLocationId = store.currentDirectoryId;

        // Try to find the directory in the cached directories
        const cachedData = MediaService.cache.get('directory-tree');
        const directories = (cachedData?.data?.directories || []) as MediaDirectory[];

        if (currentLocationId && directoryId) {
          // If we're inside a directory and know its ID
          const dirToRename = directories.find((d: MediaDirectory) => d.id === directoryId);

          if (dirToRename) {
            if (dirToRename.parent_directory_id) {
              // This is a subdirectory
              const parentDir = directories.find(
                (d: MediaDirectory) => d.id === dirToRename.parent_directory_id,
              );
              if (parentDir) {
                pathArray = [parentDir.name, dirToRename.name];
              } else {
                pathArray = [dirToRename.name];
              }
            } else {
              // Top level directory
              pathArray = [dirToRename.name];
            }
          } else if (store.directoryPathMap && store.directoryPathMap[directoryId]) {
            // Try to get from path map as last resort
            const path = store.directoryPathMap[directoryId];
            pathArray = path.split('/').filter((p: string) => p.trim() !== '');
          } else {
            // Last resort: try to get any path info we can
            const apiPath = store.apiPath || '';
            const segments = apiPath.split('/').filter((s: string) => s.trim() !== '');

            if (segments.length > 0) {
              // Use current location + directory ID as name
              pathArray = [...segments, directoryId];
            } else {
              pathArray = [directoryId];
            }
          }
        } else {
          // Absolute fallback
          pathArray = [directoryId];
          console.warn('Using directory ID as path since no context was found:', directoryId);
        }
      }

      // Make the rename API call
      const response = await axios.patch(
        `${this.beApiUrl}/ai-agent/media/folders`,
        {
          path: pathArray,
          new_name: newName,
          agent_id,
        },
        {
          headers: this.headers,
        },
      );

      // CRITICAL: Clear all media caches after rename
      // The folder structure has changed, so cached media items with old paths are now invalid
      this.clearCache();

      // Also refresh directory tree to get updated folder structure
      setTimeout(() => {
        this.getDirectoryTree().catch((err) =>
          console.error('Error refreshing directory tree after rename:', err),
        );
      }, 500);

      return response.data;
    } catch (error) {
      console.error('Error renaming directory:', error);
      throw error;
    }
  }

  // Method to find a directory by its ID in our cached data
  findDirectoryById(directoryId: string): MediaDirectory | null {
    // Check the cache for directory data
    const cachedData = MediaService.cache.get('directory-tree');
    if (cachedData?.data?.directories) {
      // Search through all directories in the cache
      const directories = cachedData.data.directories as MediaDirectory[];
      return directories.find((dir) => dir.id === directoryId) || null;
    }
    // Directory not found
    return null;
  }

  /**
   * Build the full directory path from root to target directory
   * Walks up the parent chain to construct complete path array
   * @param directoryId The target directory ID
   * @returns Array of directory names from root to target (e.g., ['parent', 'child'])
   */
  private buildFullDirectoryPath(directoryId: string): string[] {
    const store = useMediaDirectoryStore.getState();
    const cachedData = MediaService.cache.get('directory-tree');
    const cachedDirectories = (cachedData?.data?.directories || []) as MediaDirectory[];

    let directory = this.findDirectoryById(directoryId);

    // If directory not found and cache is empty, try to construct path using current context
    if (!directory && cachedDirectories.length === 0) {
      const currentDirectoryName =
        store.currentDirectoryId && store.currentDirectoryId !== 'root'
          ? store.currentDirectoryId.replace(/^dir-child-/, '').replace(/^dir-root-/, '')
          : null;

      // Check if we can determine the directory structure from store
      const storeDirectories = (store as any).directories || [];

      if (storeDirectories.length > 0) {
        const targetDir = storeDirectories.find(
          (dir: any) => dir.id === directoryId || dir.name === directoryId,
        );

        if (targetDir) {
          const targetParentId = targetDir.parent_directory_id || targetDir.parent_id;
          // If target directory has a parent that matches current directory, it's a child
          if (targetParentId && targetParentId === store.currentDirectoryId) {
            return [currentDirectoryName!, directoryId];
          } else {
            return [directoryId];
          }
        }
      }

      // Fallback: If we can't determine relationship from data, use context logic
      if (currentDirectoryName) {
        // We're inside a directory - most likely trying to delete a subdirectory
        return [currentDirectoryName, directoryId];
      } else {
        // We're at root level - assume root-level deletion
        return [directoryId];
      }
    }

    if (!directory) {
      Logger.warn(
        `Directory with ID ${directoryId} not found in cache - using fallback`,
        'MediaService',
      );
      return [directoryId]; // Return the ID as fallback
    }

    Logger.info(`📁 Target directory: ${directory.name} (ID: ${directory.id})`, 'MediaService');
    Logger.info(
      `👆 Parent directory ID: ${directory.parent_directory_id || directory.parent_id || 'ROOT'}`,
      'MediaService',
    );

    const pathSegments: string[] = [];
    let currentDir = directory;
    let depth = 0;
    const maxDepth = 10; // Prevent infinite loops

    // Walk up the parent chain to build complete path
    while (currentDir && depth < maxDepth) {
      // Add current directory name to the beginning of path
      pathSegments.unshift(currentDir.name);

      // Move to parent directory
      const parentId = currentDir.parent_directory_id || currentDir.parent_id;
      if (parentId) {
        const parentDir = this.findDirectoryById(parentId);

        if (!parentDir) {
          Logger.warn(`Parent directory with ID ${parentId} not found in cache`, 'MediaService');
          break;
        }

        if (parentDir.id === directory.id) {
          Logger.error(
            `Infinite loop detected: parent ID ${parentId} equals target ID ${directory.id}`,
            'MediaService',
          );
          break;
        }

        currentDir = parentDir;
      } else {
        break;
      }

      depth++;
    }

    if (depth >= maxDepth) {
      Logger.error(`Max depth reached (${maxDepth}) - possible infinite loop`, 'MediaService');
    }

    return pathSegments;
  }

  async deleteDirectory(directoryId: string): Promise<boolean> {
    const agent_id = await this.getAgentId();
    const pathArray = this.buildFullDirectoryPath(directoryId);

    try {
      const response = await axios.delete(`${this.beApiUrl}/ai-agent/media/folders`, {
        headers: this.headers,
        data: { path: pathArray, agent_id },
      });

      return true;
    } catch (error: any) {
      // Check for 422 status code and specific non-empty folder error message
      if (error.response && error.response.status === 422) {
        const errorMessage = error.response.data?.message || error.response.data?.error || '';

        // Only throw DirectoryError if the message contains the specific non-empty folder error
        if (errorMessage.includes(MediaService.NON_EMPTY_FOLDER_ERROR_MESSAGE)) {
          throw new DirectoryError(
            'Cannot delete directory that contains files or subdirectories. Please delete all contents first.',
            422,
          );
        }
      }

      Logger.error('Failed to delete directory', 'MediaService', error);
      throw error;
    }
  }

  /**
   * Delete a directory using an explicitly provided full path
   * This is a more direct approach when we already know the exact path
   * @param fullPath Array containing the complete path to the directory (e.g., ['parent', 'child'])
   * @returns Promise<boolean> indicating success
   */
  async deleteDirectoryWithPath(fullPath: string[]): Promise<boolean> {
    try {
      // Get agent ID before making the request
      const agent_id = await this.getAgentId();

      // Send DELETE request with the provided path in the request body
      await axios.delete(`${this.beApiUrl}/ai-agent/media/folders`, {
        headers: this.headers,
        data: {
          // Use 'data' property for DELETE request body
          path: fullPath,
          agent_id,
        },
      });
      return true;
    } catch (error: any) {
      console.error('Error deleting directory with path:', error);

      // Check for 422 status code and specific non-empty folder error message
      if (error.response && error.response.status === 422) {
        const backendErrorMessage =
          error.response.data?.message || error.response.data?.error || '';

        // Only throw DirectoryError if the message contains the specific non-empty folder error
        if (backendErrorMessage.includes(MediaService.NON_EMPTY_FOLDER_ERROR_MESSAGE)) {
          const errorMessage =
            'Cannot delete directory that contains files or subdirectories. Please delete all contents first.';
          // Create a custom error with the status code and message
          const customError = new DirectoryError(errorMessage, 422);
          throw customError;
        }
      }

      throw error;
    }
  }

  // Process the nested directory structure from API and convert to flat MediaDirectory[] format
  processDirectoryTree(directoryTree: any[]): MediaDirectory[] {
    const flatDirectories: MediaDirectory[] = [];

    // Recursive function to process the tree
    const processNode = (
      node: any,
      parentId: string | null = null,
      ancestorPath: string[] = [],
    ) => {
      const nodePath = [...ancestorPath, node.name];
      const nodeApiPath = nodePath.join('/');

      // Create the directory object
      const directory: MediaDirectory = {
        id: `dir-${node.name}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        name: node.name,
        parent_directory_id: parentId,
        parent_id: parentId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        custom_properties: {
          apiPath: nodeApiPath,
          path: nodePath,
        },
      };

      // Add to flat list
      flatDirectories.push(directory);

      // Process children recursively
      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          processNode(child, directory.id, nodePath);
        }
      }
    };

    // Process each root node
    for (const root of directoryTree) {
      processNode(root);
    }

    return flatDirectories;
  }

  /**
   * Helper method to clean API paths - ensures compatibility with backend
   * @param path The path to clean
   * @returns The cleaned path
   */
  cleanApiPath(path: string): string {
    if (!path) return '';

    // Step 1: Remove any leading slashes
    let cleanedPath = path.replace(/^\/+/, '');

    // Step 2: Remove any trailing slashes
    cleanedPath = cleanedPath.replace(/\/+$/, '');

    // Step 3: Normalize double slashes to single
    cleanedPath = cleanedPath.replace(/\/+/g, '/');

    // Step 4: Trim any whitespace
    cleanedPath = cleanedPath.trim();

    // Step 5: CRITICAL - Remove any directory prefixes (dir-child-, dir-root-) from the path
    // This ensures API requests never include these prefixes
    cleanedPath = this.cleanApiPathFromPrefixes(cleanedPath);

    return cleanedPath;
  }

  /**
   * Constructs a full API path based on the directory ID and store state
   * @param directoryId The current directory ID
   * @returns The full path for API requests
   */
  constructApiPath(directoryId: string | null | undefined): string | undefined {
    // If we don't have a directory ID, we're at the root

    if (!directoryId || directoryId === 'root') {
      return '';
    }

    // First check if we can get the path from the store
    const storeState = useMediaDirectoryStore.getState();
    const storeApiPath = storeState.apiPath;
    const storeCurrentId = storeState.currentDirectoryId;

    // If the store already has the correct path for this directory, use it
    if (storeApiPath && storeCurrentId === directoryId) {
      const cleanedPath = this.cleanApiPath(storeApiPath);

      return cleanedPath;
    }

    // If we can't get it from the store, try to build it from the directory structure
    try {
      // Just use the directory ID as a fallback

      return directoryId;
    } catch (error) {
      console.error('Error constructing API path:', error);
      // Fallback to just using the directory ID

      return directoryId;
    }
  }

  /**
   * Helper method to clean API paths by removing any directory ID prefixes
   * This ensures we always use the correct path format when making API requests
   * @param path The raw API path which might contain prefixes
   * @returns The cleaned path ready for API requests
   */
  cleanApiPathFromPrefixes(path: string): string {
    if (!path) return '';

    // Remove dir-child- prefix if present
    if (path.startsWith('dir-child-')) {
      const match = path.match(/^dir-child-(.+)$/);
      if (match && match[1]) {
        return match[1];
      }
    }

    // Remove dir-root- prefix if present (only from path segments, not the whole path)
    const segments = path.split('/');
    const cleanedSegments = segments.map((segment) => {
      if (segment.startsWith('dir-root-')) {
        return segment.substring('dir-root-'.length);
      }
      return segment;
    });

    return cleanedSegments.join('/');
  }

  cleanDirectoryIdFromPrefixes(id: string): string {
    if (!id) return '';

    if (id.includes(':')) return id.split(':')[1];
    return id;
  }

  // Convert a FormMediaObject to a real MediaObject after upload
  convertFormMediaToMediaObject(
    formMedia: FormMediaObject,
    uploadedMedia: MediaObject,
  ): MediaObject {
    return {
      ...uploadedMedia,
      id: uploadedMedia.id,
      file_name: formMedia.file?.name || uploadedMedia.file_name,
      mime_type: formMedia.file?.type || uploadedMedia.mime_type,
      size: formMedia.file?.size || uploadedMedia.size,
      // Copy over any metadata that was added during the form process
      tags: (formMedia as any).tags || uploadedMedia.tags || [],
      metadata: {
        description: (formMedia as any).description || '',
        author: (formMedia as any).author || '',
        location: (formMedia as any).location || '',
        copyright: (formMedia as any).copyright || '',
        ...((formMedia as any).metadata || {}),
      },
    };
  }

  /**
   * Fetch the complete directory tree hierarchy from the dedicated API endpoint
   * This method uses a specialized endpoint that returns the full directory structure
   * with proper parent-child relationships already established
   *
   * @param forceRefresh Whether to force a fresh API call bypassing cache
   * @returns Promise with the complete directory tree structure
   */
  async getDirectoryTree(forceRefresh = false): Promise<any> {
    // Check cache first if not forcing refresh
    const now = Date.now();
    if (
      !forceRefresh &&
      MediaService.directoryTreePromise &&
      now - MediaService.directoryTreeTimestamp < this.CACHE_TTL
    ) {
      return MediaService.directoryTreePromise;
    }

    // Reset cache and fetch fresh data
    MediaService.directoryTreeTimestamp = now;

    try {
      // Ensure we have agent ID before making the request
      const agent_id = await this.getAgentId();

      // Create the promise for the API call
      MediaService.directoryTreePromise = new Promise(async (resolve, reject) => {
        try {
          const response = await axios.get(`${this.beApiUrl}/ai-agent/media/directories`, {
            headers: this.headers,
            params: { agent_id },
          });

          // Return the directory structure directly as provided by the API
          // This is already in the correct hierarchical format
          resolve(response.data);
        } catch (error) {
          console.error('Error fetching directory tree:', error);
          reject(error);
        }
      });

      return MediaService.directoryTreePromise;
    } catch (error) {
      console.error('Error in getDirectoryTree:', error);
      throw error;
    }
  }

  /**
   * Analyzes media using AI to generate tags and descriptions
   *
   * @param mediaId - ID of the media to analyze
   * @param force - Whether to force reanalysis even if already analyzed
   * @returns Promise with the analysis response
   */
  async analyzeMedia(mediaId: string, force = false): Promise<any> {
    try {
      return await aiAgentApiService.analyzeMedia(mediaId, force);
    } catch (error) {
      console.error('Error analyzing media (MediaService):', error);
      throw error;
    }
  }

  /**
   * Checks the current analysis status of a media item
   *
   * @param mediaId - ID of the media to check
   * @returns Promise with the current analysis state
   */
  async checkAnalysisStatus(mediaId: string): Promise<any> {
    try {
      return await aiAgentApiService.checkAnalysisStatus(mediaId);
    } catch (error) {
      console.error('Error checking analysis status (MediaService):', error);
      throw error;
    }
  }

  /**
   * Polls the analysis status until completion or timeout
   * Delegates to aiAgentApiService for shared implementation
   *
   * @param mediaId - ID of the media to poll
   * @param maxAttempts - Maximum number of polling attempts (default: 60)
   * @param initialInterval - Initial polling interval in milliseconds (default: 500ms)
   * @returns Promise with the final analysis result (or latest status if timeout)
   */
  async pollAnalysisStatus(
    mediaId: string,
    maxAttempts = 60,
    initialInterval = 500,
  ): Promise<any> {
    return this.aiAgentApiService.pollAnalysisStatus(mediaId, {
      maxAttempts,
      initialInterval,
    });
  }


  /**
   * Fetch storage usage statistics from the backend
   * @returns Promise with storage usage data
   */
  async getStorageUsageStatus(): Promise<StorageUsageStatus> {
    try {
      // Ensure we have agent ID before making the request
      await this.getAgentId();

      const response = await axios.get(`${this.beApiUrl}/ai-agent/media/storage/status`, {
        headers: this.headers,
      });

      // Debug: log raw API response to trace storage data issues
      Logger.debug('📊 Storage API raw response:', 'MediaService', {
        responseData: response.data,
        hasDataWrapper: !!(response.data && response.data.data),
      });

      // Extract the data from the response
      let responseData: StorageStatusResponse;

      if (response.data && response.data.data) {
        // Handle the wrapped response format
        responseData = response.data.data;
      } else {
        // Handle direct response format
        responseData = response.data;
      }

      Logger.debug('📊 Storage parsed responseData:', 'MediaService', {
        hasGlobal: !!responseData.global,
        topLevelSizeUsed: responseData.sizeUsed,
        topLevelSizeLimit: responseData.sizeLimit,
        globalSizeUsed: responseData.global?.sizeUsed,
        globalSizeLimit: responseData.global?.sizeLimit,
      });

      // Map the API response format to our interface
      // Use global values if available, otherwise use top-level values
      const globalData = responseData.global || responseData;
      const usedStorage = globalData.sizeUsed ?? 0;
      const maxStorage = globalData.sizeLimit ?? 0;

      // Calculate percentage if limit is greater than 0, otherwise default to 0
      const percentageUsed = maxStorage > 0 ? (usedStorage / maxStorage) * 100 : 0;

      const result = {
        used_storage: usedStorage,
        max_storage: maxStorage,
        percentage_used: percentageUsed,
        fileCount: globalData.fileCount,
        // Include per-request limit for soft warnings (default 100GB if not provided)
        // Use ?? so that server-sent 0 (unlimited) is preserved
        perRequestLimit: responseData.perRequestLimit ?? DEFAULT_PER_REQUEST_LIMIT,
      };

      Logger.debug('📊 Storage final result:', 'MediaService', result);

      return result;
    } catch (error) {
      console.error('Error fetching storage usage status:', error);
      // Return default values if the API call fails
      return {
        used_storage: 0,
        max_storage: 0,
        percentage_used: 0,
        fileCount: 0,
      };
    }
  }

  async getUploadUrl(params: { file: File; userPath: string[]; brandId?: string }) {
    const { file, userPath, brandId } = params;

    const requestPayload: any = {
      file_name: file.name,
      file_size: file.size,
      user_path: userPath,
    };

    // Add brand ID as user_id if provided
    if (brandId) {
      requestPayload.user_id = brandId;
      console.debug('🔧 MediaService.getUploadUrl: Adding user_id to payload:', brandId);
    }

    // Build headers with optional brandId override for x-brand-id
    // This is needed for WebView onboarding where we skip setSelectedBrand
    const requestHeaders = { ...this.headers };
    if (brandId) {
      requestHeaders['x-brand-id'] = brandId;
      console.debug('🔧 MediaService.getUploadUrl: Overriding x-brand-id header:', brandId);
    }

    try {
      // Make the API call with a timeout to prevent hanging
      const uploadUrlResponse = await axios.post(
        `${this.beApiUrl}/ai-agent/media/get-upload-url`,
        requestPayload,
        {
          headers: requestHeaders,
          timeout: 15000, // 15 second timeout to prevent indefinite hanging
        },
      );

      const responseData = uploadUrlResponse.data;

      return {
        upload_url: responseData.upload_url,
        upload_token: responseData.upload_token,
        id: responseData.id,
      };
    } catch (apiError: any) {
      console.error('❌ Error making get-upload-url API call:', apiError.message);
      if (apiError.response) {
        console.error('Error response details:', {
          status: apiError.response.status,
          statusText: apiError.response.statusText,
          data: apiError.response.data,
        });
      } else if (apiError.request) {
        console.error('No response received from server (timeout or network issue)');
      }
      throw apiError;
    }
  }

  // ==========================================
  // PHASE 1: ROBUST FILE INFORMATION WRAPPERS
  // ==========================================

  /**
   * Comprehensive wrapper for all file information operations
   * Similar to moveAllMediaVersions, this provides robust error handling,
   * pre-fetching, and multi-operation coordination for file editing.
   */
  async saveAllFileInformationVersions(
    mediaId: string,
    updates: {
      fileName?: string;
      description?: string;
      tags?: string[];
      directoryId?: string | null;
      metadata?: any;
    },
  ): Promise<{
    success: boolean;
    results: {
      fileName?: { success: boolean; error?: any };
      metadata?: { success: boolean; error?: any };
      directory?: { success: boolean; error?: any };
      tags?: { success: boolean; error?: any };
    };
    finalData: MediaObject;
  }> {
    const logger = Logger.createScopedLogger('MediaService.saveAllFileInformationVersions');

    // 1. Pre-fetch current file data to ensure we have accurate information
    let currentMediaObject: MediaObject;
    try {
      currentMediaObject = await this.getMediaItemById(mediaId);
      logger.info('Pre-fetched current media data', { mediaId, currentData: currentMediaObject });
    } catch (error) {
      logger.error('Failed to fetch current media data, proceeding with minimal data', error);
      currentMediaObject = { id: mediaId } as MediaObject;
    }

    // 2. Initialize operation tracking
    const operationResults = {
      fileName: { success: false, error: null as any },
      metadata: { success: false, error: null as any },
      directory: { success: false, error: null as any },
      tags: { success: false, error: null as any },
    };

    let finalResult: MediaObject = currentMediaObject;
    let overallSuccess = true;

    // 3. Execute operations in order of priority (critical operations first)

    // CRITICAL: Directory move (if requested)
    if (
      updates.directoryId !== undefined &&
      updates.directoryId !== currentMediaObject.directory_id
    ) {
      try {
        logger.info('Executing directory move operation', {
          mediaId,
          targetDirectory: updates.directoryId,
        });
        finalResult = await this.moveMedia(mediaId, updates.directoryId);
        operationResults.directory = { success: true, error: null };
        logger.info('Directory move completed successfully');
      } catch (error) {
        logger.error('Directory move failed', error);
        operationResults.directory = { success: false, error };
        overallSuccess = false;
      }
    }

    // CRITICAL: File name change (if requested)
    if (
      updates.fileName !== undefined &&
      updates.fileName.trim() !== '' &&
      updates.fileName !== currentMediaObject.file_name
    ) {
      try {
        logger.info('Executing file rename operation', { mediaId, newFileName: updates.fileName });
        const renameResult = await this.renameMediaFile(mediaId, updates.fileName);
        finalResult = { ...finalResult, ...renameResult };
        operationResults.fileName = { success: true, error: null };
        logger.info('File rename completed successfully');
      } catch (error) {
        logger.error('File rename failed', error);
        operationResults.fileName = { success: false, error };
        overallSuccess = false;
      }
    }

    // IMPORTANT: Metadata updates (description, tags)
    if (
      updates.description !== undefined ||
      updates.tags !== undefined ||
      updates.metadata !== undefined
    ) {
      try {
        logger.info('Executing metadata update operation', {
          mediaId,
          hasDescription: updates.description !== undefined,
          hasTags: updates.tags !== undefined,
          hasMetadata: updates.metadata !== undefined,
        });

        // Build metadata update payload
        const metadataPayload: any = {};

        if (updates.description !== undefined) {
          metadataPayload.description = updates.description;
        }

        if (updates.tags !== undefined) {
          metadataPayload.tags = updates.tags;
        }

        if (updates.metadata !== undefined) {
          metadataPayload.metadata = updates.metadata;
        }

        const metadataResult = await this.updateMediaMetadata(mediaId, metadataPayload);
        finalResult = { ...finalResult, ...metadataResult };

        operationResults.metadata = { success: true, error: null };
        if (updates.tags !== undefined) {
          operationResults.tags = { success: true, error: null };
        }

        logger.info('Metadata update completed successfully');
      } catch (error) {
        logger.error('Metadata update failed', error);
        operationResults.metadata = { success: false, error };
        if (updates.tags !== undefined) {
          operationResults.tags = { success: false, error };
        }
        // Don't mark overall as failed for metadata - it's important but not critical
      }
    }

    // 4. Log final results
    const successCount = Object.values(operationResults).filter((r) => r.success).length;
    const totalOperations = Object.values(operationResults).filter(
      (r) => r.success !== false || r.error !== null,
    ).length;

    logger.info('All operations completed', {
      overallSuccess,
      successCount,
      totalOperations,
      operationResults,
    });

    return {
      success: overallSuccess,
      results: operationResults,
      finalData: finalResult,
    };
  }

  /**
   * Robust wrapper for file rename operations
   * Provides better error handling and state management than direct renameMediaFile calls
   */
  async renameFileRobust(mediaId: string, newFileName: string): Promise<MediaObject> {
    const logger = Logger.createScopedLogger('MediaService.renameFileRobust');

    // Pre-fetch current data
    let currentMediaObject: MediaObject;
    try {
      currentMediaObject = await this.getMediaItemById(mediaId);
    } catch (error) {
      logger.warn('Failed to fetch current media data for rename', error);
      currentMediaObject = { id: mediaId } as MediaObject;
    }

    // Attempt rename with error recovery
    try {
      const result = await this.renameMediaFile(mediaId, newFileName);
      logger.info('File rename completed successfully', { mediaId, newFileName });
      return result;
    } catch (error) {
      logger.error('File rename failed, attempting recovery', error);

      // For now, just rethrow - in future phases we can add more recovery logic
      throw error;
    }
  }

  /**
   * Robust wrapper for metadata update operations
   * Provides better error handling and partial update support
   */
  async updateMetadataRobust(
    mediaId: string,
    updates: {
      description?: string;
      tags?: string[];
      metadata?: any;
    },
  ): Promise<MediaObject> {
    const logger = Logger.createScopedLogger('MediaService.updateMetadataRobust');

    // Pre-fetch current data
    let currentMediaObject: MediaObject;
    try {
      currentMediaObject = await this.getMediaItemById(mediaId);
    } catch (error) {
      logger.warn('Failed to fetch current media data for metadata update', error);
      currentMediaObject = { id: mediaId } as MediaObject;
    }

    // Attempt metadata update with error recovery
    try {
      const result = await this.updateMediaMetadata(mediaId, updates);
      logger.info('Metadata update completed successfully', { mediaId, updates });
      return result;
    } catch (error) {
      logger.error('Metadata update failed, attempting recovery', error);

      // For now, just rethrow - in future phases we can add more recovery logic
      throw error;
    }
  }

  /**
   * Robust wrapper for directory move operations
   * Leverages the existing moveAllMediaVersions wrapper
   */
  async moveFileRobust(mediaId: string, targetDirectoryId: string | null): Promise<MediaObject> {
    const logger = Logger.createScopedLogger('MediaService.moveFileRobust');

    // Use the existing robust move wrapper
    try {
      const result = await this.moveAllMediaVersions(mediaId, targetDirectoryId);
      logger.info('File move completed successfully', { mediaId, targetDirectoryId });
      return result;
    } catch (error) {
      logger.error('File move failed', error);
      throw error;
    }
  }
}

export const mediaService = new MediaService();
export default mediaService;
